/**
 * A secret -- the family code, a child's code -- shown or typed, with a reveal toggle.
 *
 * Two jobs in one component because they are the same problem seen from each
 * side: a phrase this device generated and wants read accurately, and a phrase
 * somebody is copying in from paper. Both go wrong the same way -- a character
 * misread, and nothing explains why nothing works.
 *
 * Hidden by default -- BOTH ways round. A key phrase on screen in a kitchen is
 * a key phrase a visitor can read, and that is as true of one this device just
 * generated as of one somebody is typing back in.
 *
 * It used to start revealed whenever it was showing a generated phrase, which
 * made the checkbox arrive already ticked: clicking Show appeared to do
 * nothing, because there was nothing left for it to do. A reveal control whose
 * first click has no visible effect teaches people it is broken.
 */

import { useState } from 'react';

export function Secret({
  value,
  label,
  typed = false,
  onChange,
  onRegenerate,
}: {
  value: string;
  label?: string;
  /** True when a person is entering it rather than reading one we made. */
  typed?: boolean;
  onChange?: (value: string) => void;
  onRegenerate?: () => void;
}) {
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard permission refused, or an insecure context. Not worth an
      // error: the phrase is on screen and can be selected by hand.
    }
  }

  return (
    <div className="secret">
      {label ? <p className="field-label">{label}</p> : null}

      {typed ? (
        <input
          className="secret-input"
          type={shown ? 'text' : 'password'}
          value={value}
          onChange={(event) => onChange?.(event.target.value)}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          // NO PLACEHOLDER, deliberately. A password input renders its
          // placeholder as plain text, so anything phrase-shaped in here looks
          // exactly like a revealed value in a field whose whole job is to
          // hide one. This said "AppleEarthRiverStone472", and the result was
          // a parent staring at what they took to be their key, wondering why
          // Show did nothing and why Unlock was greyed out. The field was
          // empty the entire time.
          //
          // The label above and the note below say what to type. A field that
          // looks empty when it is empty is worth more than a hint.
        />
      ) : (
        <p className={shown ? 'secret-value' : 'secret-value hidden'}>
          {shown ? group(value) : '•'.repeat(Math.max(value.length, 12))}
        </p>
      )}

      <div className="secret-actions">
        <label className="secret-toggle">
          <input
            type="checkbox"
            checked={shown}
            onChange={(event) => setShown(event.target.checked)}
          />
          <span>Show</span>
        </label>

        {!typed ? (
          <button type="button" className="text-button" onClick={() => void copy()}>
            {copied ? 'Copied' : 'Copy'}
          </button>
        ) : null}

        {onRegenerate ? (
          <button type="button" className="text-button" onClick={onRegenerate}>
            Give me another
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Break a run-together secret apart for reading.
 *
 * AppleEarthRiverStone472 becomes Apple Earth River Stone 472, which is far
 * easier to copy onto paper a word at a time. The spaces are presentation
 * only -- the normaliser strips them on the way back in, so it does not
 * matter whether they get typed.
 *
 * A value that ALREADY has separators is left exactly as it is. A family code
 * arrives here as K7QM-3XR9-TVWX-2345-P8NB, and the letter/digit rule below
 * would read that as a word boundary and render "K 7QM-3XR9-TVWX-2345-P8NB".
 * Groups of four are already the easiest thing to read it in; there is
 * nothing here left to help with.
 */
function group(secret: string): string {
  if (/[\s-]/.test(secret)) return secret;
  return secret.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Za-z])(\d)/g, '$1 $2');
}
